///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { format, parseISO } from "date-fns";
import { find, includes, max } from "lodash";

import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Text,
  LineChart,
  Line,
  Legend
} from "recharts";
import messages from "../../../helpers/messages";
import {
  dateFormat,
  division as divisionTypes,
  graphTypesV2,
  lineGraphSeries,
  monthsAbbreviations
} from "../../../helpers/types";

const getDomainForXAxis = (graphData, startDate) => {
  let data = graphData
    ? [
        ...graphData?.find(x => x.name === lineGraphSeries.historical).data,
        ...graphData?.find(x => x.name === lineGraphSeries.forecasted).data
      ]
    : [];
  let current = format(parseISO(startDate), dateFormat.yyyMM);
  let end = max(data?.map(d => d.yearMonth));
  let months = [];

  while (months.length <= 11 || (end && current <= end)) {
    months.push(current);
    let year = parseInt(current.split("-")[0], 10);
    let month = parseInt(current.split("-")[1], 10);
    if (month === 12) {
      year += 1;
      month = 1;
    } else {
      month += 1;
    }
    current = `${year}-${("0" + month).slice(-2)}`;
  }
  return months;
};

const getRecentHistoricalDataForForecastPoint = (data, payload) => {
  let historicalData;
  const historicalSeries = data?.find(
    x => x.name === lineGraphSeries.historical
  ).data;
  let recentYear = max(historicalSeries.map(x => parseInt(x.year, 10)));

  while (
    historicalData === undefined &&
    includes(
      historicalSeries.map(x => parseInt(x.year, 10)),
      recentYear
    )
  ) {
    historicalData = find(historicalSeries, {
      year: `${recentYear}`,
      month: `${payload.month}`
    });

    recentYear = recentYear - 1;
  }

  return historicalData;
};

const CustomTooltip = props => {
  const { active, yAxisUnit, division, data, label, graphType, activeDot } =
    props;
  const activeSeries = activeDot && data?.find(x => x.name === activeDot).data;
  const payload = activeSeries?.find(x => x.yearMonth === label);
  let historicalData;

  if (active && payload) {
    if (activeDot === lineGraphSeries.forecasted) {
      historicalData = getRecentHistoricalDataForForecastPoint(data, payload);
    }

    return (
      <div className="tooltip card p-12 pb-8">
        <p className="mb-4">
          {`${monthsAbbreviations[payload.month - 1]} ${payload.year}`}
        </p>
        {graphType === graphTypesV2.costOverTime && (
          <>
            <p>
              {activeDot === lineGraphSeries.historical
                ? messages.avgCost.defaultMessage
                : messages.forecastedCost.defaultMessage}
              : {yAxisUnit}
              {payload.averageCost}
            </p>
            {activeDot === lineGraphSeries.forecasted && historicalData && (
              <p>
                {`${historicalData.year} ${messages.historicalCost.defaultMessage}: ${yAxisUnit}${historicalData.averageCost}`}
              </p>
            )}
          </>
        )}
        {graphType === graphTypesV2.cpmOverTime && (
          <>
            {activeDot === lineGraphSeries.historical ? (
              <p>
                {division === divisionTypes.coy
                  ? messages.avgCpm.defaultMessage
                  : messages.avgCpKm.defaultMessage}
                : {yAxisUnit}
                {payload.averageCpm}
              </p>
            ) : (
              <p>
                {messages.forecastedCpm.defaultMessage}: {yAxisUnit}
                {payload.averageCpm}
              </p>
            )}
            {activeDot === lineGraphSeries.forecasted && historicalData && (
              <p>
                {`${historicalData.year} ${messages.historicalCpm.defaultMessage}: ${yAxisUnit}${historicalData.averageCpm}`}
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  return null;
};

export const LineGraph = ({
  data,
  yAxisDataKey,
  yAxisName,
  currency,
  width = "100%",
  division,
  value
}) => {
  const {
    analytics: {
      filters: { startDate, endDate }
    }
  } = useSelector(state => state);
  const [activeDot, setActiveDot] = useState(null);

  const formatTicker = tick => {
    const month = parseInt(tick.split("-")[1], 10);
    return monthsAbbreviations[month - 1] + " - " + tick.split("-")[0];
  };

  return (
    <>
      <ResponsiveContainer height={265} width={width} className="line-graph">
        <LineChart
          margin={{
            top: 55,
            right: 20,
            bottom: 10,
            left: 5
          }}
        >
          <CartesianGrid stroke="#EFF2FA" />
          <XAxis
            type="category"
            dataKey="yearMonth"
            allowDuplicatedCategory={false}
            axisLine={false}
            tickLine={false}
            width={30}
            tickFormatter={formatTicker}
            domain={getDomainForXAxis(data, startDate, endDate)}
            interval="preserveStart"
            dy={15}
          />
          <YAxis
            type="number"
            dataKey={yAxisDataKey}
            name={yAxisName}
            axisLine={false}
            tickLine={false}
            label={
              <Text
                className="yaxis-label"
                x={-22}
                y={-15}
                dx={50}
                dy={40}
                offset={0}
                angle={0}
                fill="#485465"
              >
                {`${yAxisName} (${currency})`}
              </Text>
            }
            width={40}
            domain={["dataMin", "dataMax"]}
          />
          <Line
            dataKey={yAxisDataKey}
            name={lineGraphSeries.historical}
            stroke="#2C9332"
            strokeWidth={2}
            fill="#2C9332"
            dot={{ r: 1 }}
            data={data?.find(x => x.name === lineGraphSeries.historical).data}
            tooltipType="none"
            activeDot={{
              onMouseOver: e => setActiveDot(lineGraphSeries.historical)
            }}
            isAnimationActive={false}
          />
          {data?.find(x => x.name === lineGraphSeries.forecasted).data
            .length && (
            <Line
              dataKey={yAxisDataKey}
              name={lineGraphSeries.forecasted}
              stroke="#0088D1"
              strokeWidth={2}
              fill="#0088D1"
              strokeDasharray="2 2"
              dot={{ r: 1 }}
              data={data?.find(x => x.name === lineGraphSeries.forecasted).data}
              activeDot={{
                onMouseOver: e => setActiveDot(lineGraphSeries.forecasted)
              }}
              isAnimationActive={false}
            />
          )}
          <Tooltip
            content={
              <CustomTooltip
                yAxisUnit={currency}
                division={division}
                data={data}
                graphType={value}
                activeDot={activeDot}
              />
            }
          />
          <Legend iconSize={24} iconType="plainline" />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default LineGraph;
